// ============================================================================
// ASISTENCIA DEL MAESTRO en Pasar lista — Karla 29-sep-2026:
//   «Falta la casilla de asistencia al maestro» → vino / no vino / mandó suplente, y que pase
//   DIRECTO a la nómina. Escribe la misma excepción que Finanzas → Nómina (no hay dato aparte).
//   · Se guarda al tocar (si el maestro no vino puede que no haya lista que guardar).
//   · Recepción marca; el maestro en su portal solo lo ve (`can_edit` lo decide el backend).
// 19-oct-2026 (Mabel): en la vista «Fotos» la maestra sale como TARJETA con su foto, la primera de la
//   galería. Las reglas viven UNA vez en `useTeacherAttendance`; la fila (vista Lista) y la tarjeta
//   (vista Fotos) solo pintan. La tarjeta NO entra en «Todos asistieron» ni en el POST de la lista.
// ============================================================================
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, X, Repeat } from 'lucide-react';
import { getTeacherStatus, setTeacherStatus, TeacherStatus, TeacherAttendanceStatus } from '../api';
import { teacherPhotoUrl } from '../../teachers/api';
import PersonAvatar from '../../../components/PersonAvatar';
import { useToast } from '../../../context/ToastContext';

const OPCIONES: { v: TeacherAttendanceStatus; label: string; Icon: typeof Check; on: string }[] = [
  { v: 'came', label: 'Vino', Icon: Check, on: 'bg-emerald-600 text-white border-emerald-600' },
  { v: 'absent', label: 'No vino', Icon: X, on: 'bg-red-600 text-white border-red-600' },
  { v: 'substitute', label: 'Suplente', Icon: Repeat, on: 'bg-sky-600 text-white border-sky-600' },
];

const hora = (ts: string | null) => {
  if (!ts) return '';
  const d = new Date(ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z');
  return isNaN(d.getTime()) ? '' : d.toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

/** Estado y acciones de la asistencia del maestro de UNA clase en UN día (una sola fuente de reglas). */
export function useTeacherAttendance(classId: number, date: string) {
  const toast = useToast();
  const [st, setSt] = useState<TeacherStatus | null>(null);
  const [saving, setSaving] = useState(false);
  const [eligiendo, setEligiendo] = useState(false);
  const [subId, setSubId] = useState<number | ''>('');

  const load = useCallback(() => {
    getTeacherStatus(classId, date)
      .then((r) => { setSt(r.data); setEligiendo(false); setSubId(r.data.substitute?.id ?? ''); })
      .catch(() => setSt(null));
  }, [classId, date]);
  useEffect(() => { load(); }, [load]);

  async function guardar(status: TeacherAttendanceStatus, substitute_user_id?: number) {
    setSaving(true);
    try {
      const r = await setTeacherStatus(classId, { date, status, substitute_user_id });
      toast.success(status === 'came' ? 'Maestro: vino' : status === 'absent' ? 'Maestro: no vino · no se le paga esta clase' : 'Suplente registrado en la nómina');
      if (r.warning) toast.info(r.warning);
      load();
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSaving(false); }
  }

  function elegir(v: TeacherAttendanceStatus) {
    if (!st) return;
    if (v === 'substitute') { setEligiendo(true); return; }
    // Mabel 19-oct: «Vino» es el estado por defecto (se paga por horario, no se guarda nada); tocarlo sin aviso parecía
    // que «no se acredita».
    if (v === st.status) {
      if (v === 'came') toast.info('Ya cuenta como que vino: se le paga esta clase en Nómina por su horario.');
      return;
    }
    guardar(v);
  }

  function cancelarSuplente() { setEligiendo(false); setSubId(st?.substitute?.id ?? ''); }

  const d0 = new Date();
  const hoy = `${d0.getFullYear()}-${String(d0.getMonth() + 1).padStart(2, '0')}-${String(d0.getDate()).padStart(2, '0')}`;
  // Una clase que todavía no pasa no se marca (el backend también lo rechaza).
  const editable = !!st && st.can_edit && st.has_session && date <= hoy;

  return { st, saving, eligiendo, subId, setSubId, elegir, guardar, cancelarSuplente, editable };
}
export type TeacherAttendanceCtl = ReturnType<typeof useTeacherAttendance>;

const etiquetaEstado = (st: TeacherStatus) =>
  st.status === 'came' ? (st.has_session ? 'Vino' : 'Sin clase este día') : st.status === 'absent' ? 'No vino' : `Suplente: ${st.substitute?.name || '?'}`;
const chipEstado = (st: TeacherStatus) =>
  st.status === 'came' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : st.status === 'absent' ? 'bg-red-500/15 text-red-600 dark:text-red-400' : 'bg-sky-500/15 text-sky-700 dark:text-sky-400';

/** Selector «¿Quién dio la clase?» — el mismo en la fila y en la tarjeta. */
function SelectorSuplente({ ctl, compacto = false }: { ctl: TeacherAttendanceCtl; compacto?: boolean }) {
  const { st, subId, setSubId, saving, guardar, cancelarSuplente } = ctl;
  if (!st) return null;
  return (
    <div className={compacto ? 'mt-2 space-y-1.5' : 'mt-3 flex flex-wrap items-center gap-2'}>
      <select value={subId} onChange={(e) => setSubId(e.target.value ? Number(e.target.value) : '')} aria-label="Suplente"
        className={`${compacto ? 'w-full text-xs' : 'min-w-[14rem] flex-1 text-sm'} rounded-lg border border-line bg-surface px-2 py-1.5 text-content outline-none focus:border-brand-blue`}>
        <option value="">{compacto ? '¿Quién la dio?' : '¿Quién dio la clase?'}</option>
        {st.teachers.map((x) => <option key={x.id} value={x.id}>{x.name}{x.has_rate ? '' : ' (sin tarifa)'}</option>)}
      </select>
      <div className={compacto ? 'grid grid-cols-2 gap-1' : 'flex gap-1.5'}>
        <button disabled={!subId || saving} onClick={() => subId && guardar('substitute', subId)}
          className={`rounded-lg bg-accent ${compacto ? 'px-1' : 'px-3'} py-1.5 text-xs font-semibold text-on-accent disabled:opacity-50`}>{compacto ? 'Guardar' : 'Guardar suplente'}</button>
        <button onClick={cancelarSuplente}
          className={`rounded-lg border border-line ${compacto ? 'px-1' : 'px-3'} py-1.5 text-xs text-content-2 hover:bg-surface-2`}>Cancelar</button>
      </div>
    </div>
  );
}

/** Vista LISTA: fila arriba del roster (igual que desde el 29-sep). */
// `verFoto`: solo quien ve Profesores puede pedir la foto (en el portal del maestro daría 403 → se queda la inicial).
export default function TeacherAttendance({ ctl, verFoto = false }: { ctl: TeacherAttendanceCtl; verFoto?: boolean }) {
  const { st, saving, eligiendo, elegir, editable } = ctl;
  if (!st || !st.teacher) return null;
  const t = st.teacher;

  return (
    <div className="mb-4 rounded-xl border border-line bg-surface-2/60 p-3">
      <div className="flex flex-wrap items-center gap-3">
        <PersonAvatar contactId={null} hasPhoto={verFoto && !!t.has_photo} photoSrc={teacherPhotoUrl(t.id)} name={t.name} size="h-10 w-10" />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-content-3">Maestro</div>
          <div className="truncate text-sm font-semibold text-content">{t.name}</div>
        </div>
        {editable ? (
          <div className="flex flex-wrap gap-1.5">
            {OPCIONES.map(({ v, label, Icon, on }) => {
              const activo = (eligiendo ? 'substitute' : st.status) === v;
              return (
                <button key={v} disabled={saving} onClick={() => elegir(v)}
                  className={`flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition disabled:opacity-60 ${activo ? on : 'border-line bg-surface text-content-2 hover:bg-surface-2'}`}>
                  <Icon size={13} /> {label}
                </button>
              );
            })}
          </div>
        ) : (
          <span className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${chipEstado(st)}`}>{etiquetaEstado(st)}</span>
        )}
      </div>

      {editable && eligiendo && <SelectorSuplente ctl={ctl} />}

      {!eligiendo && st.status === 'substitute' && st.substitute && editable && (
        <div className="mt-2 text-xs text-sky-700 dark:text-sky-400">La dio <b>{st.substitute.name}</b> · se le paga con su tarifa</div>
      )}
      {editable && !eligiendo && st.status === 'came' && (
        <div className="mt-2 text-[11px] text-content-3">
          Cuenta como dada por su horario y se le paga en Nómina. Marca solo si <b>no vino</b> o <b>mandó suplente</b>.
        </div>
      )}
      {st.marked_by && st.status !== 'came' && (
        <div className="mt-1 text-[11px] text-content-3">Marcó {st.marked_by}{st.marked_at ? ` · ${hora(st.marked_at)}` : ''}</div>
      )}
      {st.can_edit && !st.has_session && (
        <div className="mt-1 text-[11px] text-content-3">Esta clase no tiene sesión este día.</div>
      )}
    </div>
  );
}

/** Vista FOTOS: la maestra como primera tarjeta de la galería, con sus 3 estados en la propia tarjeta. */
export function TeacherAttendanceCard({ ctl, canOpenProfile }: { ctl: TeacherAttendanceCtl; canOpenProfile: boolean }) {
  const { st, saving, eligiendo, elegir, editable } = ctl;
  if (!st || !st.teacher) return null;
  const t = st.teacher;
  const [nombre, ...resto] = t.name.split(' ');
  const ring = !st.has_session ? 'ring-1 ring-line' : st.status === 'came' ? 'ring-2 ring-emerald-500' : st.status === 'absent' ? 'ring-2 ring-red-500' : 'ring-2 ring-sky-500';
  const nombreEl = (
    <>
      <span className="block truncate text-[13px] font-semibold text-white">{nombre}</span>
      <span className="block truncate text-[11px] text-white/70">{resto.join(' ')}</span>
    </>
  );

  return (
    <div className={`relative flex flex-col overflow-hidden rounded-xl bg-surface-2 ${ring}`} data-testid="maestra-tarjeta">
      <div className="relative">
        <PersonAvatar contactId={null} hasPhoto={canOpenProfile && !!t.has_photo} photoSrc={teacherPhotoUrl(t.id)} name={t.name}
          size="aspect-square w-full block" rounded="rounded-none" initialClass="font-display text-4xl"
          imgClass={`transition ${st.status === 'absent' ? 'opacity-40 grayscale' : ''}`} ringClass="fill-surface-2" title="" />
        <span className="absolute left-1.5 top-1.5 rounded-full bg-accent-solid px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-on-accent shadow">Maestra/o</span>
        {st.has_session && (
          <span className={`absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full text-white shadow ${st.status === 'came' ? 'bg-emerald-600' : st.status === 'absent' ? 'bg-red-600' : 'bg-sky-600'}`}>
            {st.status === 'came' ? <Check size={16} /> : st.status === 'absent' ? <X size={16} /> : <Repeat size={14} />}
          </span>
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/80 to-transparent" />
        <div className="absolute bottom-1.5 left-2 right-2">
          {canOpenProfile ? (
            <Link to={`/profesores/${t.id}?t=asistencia`} className="inline-block max-w-full rounded decoration-white/70 hover:underline">{nombreEl}</Link>
          ) : <span className="inline-block max-w-full">{nombreEl}</span>}
        </div>
      </div>

      <div className="flex flex-1 flex-col justify-end p-1 sm:p-1.5">
        {editable ? (
          <>
            <div className="grid grid-cols-3 gap-0.5 sm:gap-1">
              {OPCIONES.map(({ v, label, Icon, on }) => {
                const activo = (eligiendo ? 'substitute' : st.status) === v;
                return (
                  <button key={v} disabled={saving} onClick={() => elegir(v)} aria-label={`Maestra/o: ${label}`}
                    className={`flex min-w-0 flex-col items-center gap-0.5 overflow-hidden rounded-lg border px-0 py-1 text-[10px] font-medium leading-tight tracking-tight sm:font-semibold transition disabled:opacity-60 ${activo ? on : 'border-line bg-surface text-content-2 hover:bg-surface-2'}`}>
                    <Icon size={14} /> <span className="max-w-full whitespace-nowrap">{label}</span>
                  </button>
                );
              })}
            </div>
            {eligiendo && <SelectorSuplente ctl={ctl} compacto />}
            {!eligiendo && st.status === 'substitute' && st.substitute && (
              <div className="mt-1 truncate text-[11px] text-sky-700 dark:text-sky-400" title={st.substitute.name}>La dio <b>{st.substitute.name}</b></div>
            )}
          </>
        ) : (
          <span className={`block truncate rounded-lg px-2 py-1 text-center text-[11px] font-semibold ${chipEstado(st)}`}>{etiquetaEstado(st)}</span>
        )}
      </div>
    </div>
  );
}
